import pickBy from 'lodash/pickBy';

import {INVE_FIELDS} from '@/constants/inve';
import {DocumentData} from '@/engine';
import {Booleans} from '@/model/src/values/dv-boolean';
import {Investor, InvestorAccount} from '@/types/client-info';
import {mapClientSocialSecurityNumber} from '@/utils/client-info/map-social-security-number';
import {mapBankAccountsToINVE} from '@/utils/client-info/mappers/map-bank-accounts';
import {
  mapClientAddress,
  mapClientGenderToDomainValue,
  mapClientLanguageToDomainValue,
  mapClientPhone,
  mapClientProvinceCodeToDomainValue,
  mapClientCountryCodeToDomainValue,
  mapClientPostalCode,
  mapAccountTypeToDomainValue,
  mapGuaranteeToDomainValue,
  mapProductTypeToDomainValue,
  mapExistingContractsToINVE,
} from './mappers';

export const mapClientInfoToINVE = (investor: Investor, account?: InvestorAccount): DocumentData['data'] => {
  const firstAddress = (investor.addresses ?? [])[0];

  return pickBy(
    {
      [INVE_FIELDS.INVE0_investorNo]: investor.investorNo.toString(),
      [INVE_FIELDS.INVE1_language]: mapClientLanguageToDomainValue(investor.language),
      [INVE_FIELDS.INVE2_lastName]: investor.lastName,
      [INVE_FIELDS.INVE3_firstName]: investor.firstName,
      [INVE_FIELDS.INVE4_socialSecurityNumber]: mapClientSocialSecurityNumber(investor?.sin),
      [INVE_FIELDS.INVE5_occupation]: investor.occupation,
      [INVE_FIELDS.INVE6_gender]: mapClientGenderToDomainValue(investor.gender),
      [INVE_FIELDS.INVE7_address]: mapClientAddress(firstAddress),
      [INVE_FIELDS.INVE8_appNumber]: firstAddress?.appNumber,
      [INVE_FIELDS.INVE9_city]: firstAddress?.city,
      [INVE_FIELDS.INVE10_countryCode]: mapClientCountryCodeToDomainValue(firstAddress?.countryCode),
      [INVE_FIELDS.INVE11_provinceCode]: mapClientProvinceCodeToDomainValue(firstAddress?.provinceCode),
      [INVE_FIELDS.INVE12_postalCode]: mapClientPostalCode(firstAddress?.postalCode),
      [INVE_FIELDS.INVE13_phone]: mapClientPhone(investor.phones?.[0]),
      [INVE_FIELDS.INVE14_birthdate]: investor.birthdate,
      [INVE_FIELDS.INVE15_email]: investor.email,
      [INVE_FIELDS.INVE16_accountNo]: account?.accountNo?.toString(),
      [INVE_FIELDS.INVE17_accountType]: mapAccountTypeToDomainValue(account?.acctType),
      [INVE_FIELDS.INVE18_productType]: mapProductTypeToDomainValue(account?.productType),
      [INVE_FIELDS.INVE19_guarantee]: mapGuaranteeToDomainValue(account?.guaranty),
      [INVE_FIELDS.INVE20_spouseLastName]: investor.spouse?.lastName,
      [INVE_FIELDS.INVE21_spouseFirstName]: investor.spouse?.firstName,
      [INVE_FIELDS.INVE22_spouseOccupation]: investor.spouse?.occupation,
      [INVE_FIELDS.INVE23_spouseGender]: mapClientGenderToDomainValue(investor.spouse?.gender),
      [INVE_FIELDS.INVE24_spouseAddress]: mapClientAddress(investor.spouse?.addresses?.[0]),
      [INVE_FIELDS.INVE25_spouseAppNumber]: investor.spouse?.addresses?.[0]?.appNumber,
      [INVE_FIELDS.INVE26_spouseCity]: investor.spouse?.addresses?.[0]?.city,
      [INVE_FIELDS.INVE27_spouseCountryCode]: mapClientCountryCodeToDomainValue(
        investor.spouse?.addresses?.[0]?.countryCode,
      ),
      [INVE_FIELDS.INVE28_spouseProvinceCode]: mapClientProvinceCodeToDomainValue(
        investor.spouse?.addresses?.[0]?.provinceCode,
      ),
      [INVE_FIELDS.INVE29_spousePostalCode]: mapClientPostalCode(investor.spouse?.addresses?.[0]?.postalCode),
      [INVE_FIELDS.INVE30_spousePhone]: mapClientPhone(investor.spouse?.phones?.[0]),
      [INVE_FIELDS.INVE31_spouseBirthdate]: investor.spouse?.birthdate,
      [INVE_FIELDS.INVE32_spouseEmail]: investor.spouse?.email,
      [INVE_FIELDS.INVE33_spouseLanguage]: mapClientLanguageToDomainValue(investor.spouse?.language),
      ...mapBankAccountsToINVE(investor.bankAccounts ?? []),
      ...mapExistingContractsToINVE(investor.accounts ?? []),
      [INVE_FIELDS.INVE_IsPrefilledClient]: {
        $ref: Booleans.True,
      },
    },
    value => value !== undefined && value !== null,
  );
};
